// The RFP card in Home chat, version 2 (Lane 1439, Ryan's approved round 3 mockup:
// docs/audits/2026-11-01_lane1439_rfp-card/mockup-round3.html). Only while
// sourcingIntakeCardV2Enabled is on; off, Home renders SourcingIntakeCard.js unchanged.
//
// Same data, same calls, a new layout. Every figure and word on it comes from the card
// the server wrote (SourcingIntakeCard.js's card); the build, the goal rules, the
// discard call and the open-drafts question are today's. What is new:
//   * four figure tiles, a goal block with Edit, three rows that open on click;
//   * "Make changes" turns the Home chat box into the change box (IntakeChangeBar);
//     every suggestion goes through Home's normal send, so the answers are unchanged;
//   * an earlier card of the same RFP draft collapses to one line;
//   * "Start a new chat" and the draft bar above the chat box (IntakeDraftBar, which
//     reuses the banner's resume call to bring the draft into the new chat).
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config';
import { formatMoney, formatWindow } from './sourcingApi';
import { GOAL_MAX_OWN_WORDS, GOAL_RAW_MAX, cleanGoal, goalLength } from './goal';
import { draftReason, postIntake, startedNew } from './SourcingIntakeCard';
import './SourcingIntakeCardV2.css';

const DISMISSED_TEXT = 'RFP draft put aside. Ask again in chat to start one.';

function count(n) {
  return Number(n || 0).toLocaleString();
}

// For each RFP draft (intake_id), the place in the chat of its newest card. Every
// earlier card of that draft was replaced (its revision moved), so it collapses.
export function newestIntakeCards(messages) {
  const newest = {};
  (messages || []).forEach((m, idx) => {
    const id = m && m.sourcingIntakeCard && m.sourcingIntakeCard.intake_id;
    if (id) newest[id] = idx;
  });
  return newest;
}

// The change box's choices (Ryan, round 3): four topics, each with 2 to 4 suggestions.
// Only what the RFP draft can already change in chat today (the sourcing_intake tool:
// parts added or dropped, sites dropped, suppliers added or removed by name, the
// offers due date, rounds 1 to 3, the award date, the goal). A suggestion that needs
// a name or a date fills the chat box (`fill`); a complete one is sent (`send`).
// The one card the chat can still change: the newest RFP card of this chat (a chat
// holds one live draft at a time), unless it was built (from its own Build button, or
// a later answer says so after a typed yes). -1 when
// there is none. Every other card has no Make changes: a change typed from it would
// reach whichever draft is live, not the one it shows (Codex r1).
export function liveIntakeCardIndex(messages) {
  let last = -1;
  (messages || []).forEach((m, idx) => {
    if (m && m.sourcingIntakeCard && m.sourcingIntakeCard.intake_id) last = idx;
  });
  if (last < 0 || messages[last].sourcingIntakeBuiltEvent) return -1;
  const built = (messages || []).some((m, idx) => idx > last && m && m.sourcingIntakeBuilt);
  return built ? -1 : last;
}

export const INTAKE_CHANGE_TOPICS = [
  {
    key: 'parts', label: 'Add or remove parts', placeholder: 'Which parts should change?',
    chips: [
      { label: 'Add a part', fill: 'Add part number ' },
      { label: 'Remove a part', fill: 'Remove part number ' },
      { label: 'Leave out a site', fill: 'Leave out the parts at ' },
    ],
  },
  {
    key: 'suppliers', label: 'Add or remove suppliers', placeholder: 'Which suppliers should change?',
    chips: [
      { label: 'Add a supplier', fill: 'Add the supplier ' },
      { label: 'Remove a supplier', fill: 'Remove the supplier ' },
      { label: 'Approved suppliers not included', send: 'Which approved suppliers of these parts are not in the RFP draft?' },
    ],
  },
  {
    key: 'timing', label: 'Change the timing', placeholder: 'When are offers due, and how many rounds?',
    chips: [
      { label: 'Set the offers due date', fill: 'Offers are due on ' },
      { label: 'One round', send: 'Plan one round of offers.' },
      { label: 'Up to three rounds', send: 'Plan up to three rounds of offers.' },
      { label: 'Set an award date', fill: 'Award by ' },
    ],
  },
  {
    key: 'goal', label: 'Change the goal', placeholder: 'What should the goal say?',
    chips: [
      { label: 'Rewrite the goal', fill: 'Change the goal to: ' },
      { label: 'Add to the goal', fill: 'Add to the goal: ' },
    ],
  },
];

export const INTAKE_CHANGE_PLACEHOLDER = 'Pick one, or just describe the change';

export function intakeChangePlaceholder(topicKey) {
  const topic = INTAKE_CHANGE_TOPICS.find((t) => t.key === topicKey);
  return topic ? topic.placeholder : INTAKE_CHANGE_PLACEHOLDER;
}

function Chevron() {
  return (
    <svg className="srce2-chev" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// One row that opens on click: a label, one line of value, an optional tag, a chevron.
function Row({ id, label, value, tag, open, onToggle, children }) {
  return (
    <>
      <button type="button" className={`srce2-row${open ? ' is-open' : ''}`} aria-expanded={open}
        aria-controls={`${id}-more`} onClick={onToggle} data-testid={`srce2-row-${label.toLowerCase()}`}>
        <span className="srce2-row-k">{label}</span>
        <span className="srce2-row-v">{value}</span>
        {tag && <span className="srce2-tag">{tag}</span>}
        <Chevron />
      </button>
      {open && <div className="srce2-more" id={`${id}-more`}>{children}</div>}
    </>
  );
}

function supplierSource(source) {
  if (source === 'today') return 'Sells you these parts today';
  if (source === 'added') return 'Added by you';
  return '';
}

// A replaced card: one line (Ryan: only the newest card is the full card).
function ReplacedLine({ card }) {
  const p = card.parts || {};
  return (
    <div className="srce2-replaced" data-testid="srce-intake-replaced">
      <span>Earlier version</span>
      <span aria-hidden="true">&middot;</span>
      <span>{count(p.parts)} {p.parts === 1 ? 'part' : 'parts'}</span>
      <span aria-hidden="true">&middot;</span>
      <span>{formatMoney(p.spend)}</span>
      <span aria-hidden="true">&middot;</span>
      <span>Replaced</span>
    </div>
  );
}

export default function SourcingIntakeCardV2({
  card, conversationId, authFetch, replaced = false, busy = false, changing = false,
  canChange = true, builtEvent = null, onMakeChanges, onStartNewChat, onDiscarded, onBuilt,
}) {
  const navigate = useNavigate();
  const [state, setState] = useState('open');   // open | building | dismissed
  const openDrafts = card.open_drafts || [];
  const [startNew, setStartNew] = useState(startedNew.has(card.intake_id));
  const askFirst = openDrafts.length > 0 && !startNew;
  const [error, setError] = useState('');
  // Lane 1375: Wingman's goal statement, which the buyer may edit before Build.
  const [goal, setGoal] = useState(card.goal || '');
  const [editingGoal, setEditingGoal] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const moreRef = useRef(null);
  const goalRef = useRef(null);
  const p = card.parts || {};
  const cleaned = cleanGoal(goal, true);
  const edited = cleaned !== cleanGoal(card.goal || '', true);
  const goalLen = goalLength(cleaned);
  const goalBad = !cleaned || goalLen > GOAL_MAX_OWN_WORDS;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const esc = (e) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      if (moreRef.current) moreRef.current.focus();   // keyboard users keep their place
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (editingGoal && goalRef.current) goalRef.current.focus();
  }, [editingGoal]);

  if (replaced) return <ReplacedLine card={card} />;
  // Built from this card's own Build button (Codex r2): back on Home it is one line
  // with the way to the event, never an RFP draft that still offers changes.
  if (builtEvent) {
    return (
      <div className="srce2-replaced srce2-built" data-testid="srce-intake-built-line">
        <span>Built as a draft event: {card.name}</span>
        <span className="srce2-grow" />
        <button type="button" className="srce2-btn srce2-btn--sm" onClick={() => navigate(`/sourcing/${builtEvent}`)}>
          Open the draft event
        </button>
      </div>
    );
  }

  // Today's build call, word for word (SourcingIntakeCard.js).
  const build = async () => {
    if (goalBad) return;
    setState('building');
    setError('');
    try {
      const out = await postIntake(authFetch, '/api/sourcing/intakes/build', {
        summary_token: card.token, conversation_id: conversationId,
        ...(edited ? { goal: cleaned } : {}),
      });
      if (onBuilt) onBuilt(card, out);
      navigate(`/sourcing/${out.event_public_id}`);
    } catch (e) {
      setError(e.message);
      setState('open');
    }
  };
  // Today's "Not now" call, now "Discard this draft" in the menu.
  const discard = async () => {
    setMenuOpen(false);
    setError('');
    try {
      await postIntake(authFetch, `/api/sourcing/intakes/${encodeURIComponent(card.intake_id)}/dismiss`);
      setState('dismissed');
      if (onDiscarded) onDiscarded(card);
    } catch (e) {
      setError(e.message);
    }
  };

  if (state === 'dismissed') {
    return <div className="srce-intake srce-intake--quiet">{DISMISSED_TEXT}</div>;
  }

  const toggle = (key) => setOpenRow((cur) => (cur === key ? null : key));
  const suppliers = card.suppliers || [];
  const supplierNames = suppliers.map((s) => s.name);
  const supplierLine = suppliers.length === 0 ? 'No suppliers yet'
    : `${count(suppliers.length)} ${suppliers.length === 1 ? 'supplier' : 'suppliers'}: `
      + supplierNames.slice(0, 3).join(', ')
      + (suppliers.length > 3 ? `, +${count(suppliers.length - 3)}` : '');
  const examples = p.examples || [];
  const partsLine = examples.length > 0 ? `Largest by spend: ${examples.join(', ')}`
    : `${count(p.parts)} ${p.parts === 1 ? 'part' : 'parts'} at ${count(p.site_count)} ${p.site_count === 1 ? 'site' : 'sites'}`;
  const timing = card.timing || [];
  const hasDue = timing.some((t) => t.startsWith('Offers due'));
  const timingLine = hasDue ? timing.join(' · ')
    : ['Offers due date not set', ...timing].join(' · ');
  const windowText = p.window_text || formatWindow(p.window);
  const disabled = state === 'building';
  const rowId = `srce2-${card.intake_id}-${card.revision || 0}`;

  return (
    <section className="srce2" data-testid="srce-intake-card-v2" data-revision={card.revision || undefined}
      aria-label="RFP draft">
      <header className="srce2-head">
        <span className="srce2-icon" aria-hidden="true">RFP</span>
        <div className="srce2-title">
          <div className="srce2-kicker">New RFP draft</div>
          {/* Lane 1438: the title is the event's name, exactly (one value). */}
          <div className="srce2-name" title={card.name}>{card.name}</div>
        </div>
        {/* No "Version N" (Ryan, 2026-11-01): the draft's revision counts every saved
            change, so the number jumps (reopening alone moved it 2 -> 3). */}
      </header>

      {askFirst && (
        <div className="srce2-drafts" role="status" data-testid="srce-intake-open-drafts">
          <div>You already have {openDrafts.length === 1 ? 'a draft RFP that may be this one' : 'draft RFPs that may be this one'}:</div>
          <ul className="srce2-draftlist">
            {openDrafts.map((d) => (
              <li key={d.event_public_id}>
                <span className="srce2-draftname">{d.name} ({draftReason(d)})</span>
                <button type="button" className="srce2-btn srce2-btn--sm" onClick={() => navigate(`/sourcing/${d.event_public_id}`)}>Open it</button>
              </li>
            ))}
          </ul>
          <div>
            <button type="button" className="srce2-btn srce2-btn--sm"
              onClick={() => { startedNew.add(card.intake_id); setStartNew(true); }}>Start a new one</button>
          </div>
        </div>
      )}

      <div className="srce2-figs">
        <div className="srce2-fig" data-testid="srce2-fig-parts">
          <div className="srce2-fig-l">Parts</div>
          <div className="srce2-fig-v">{count(p.parts)}</div>
        </div>
        <div className="srce2-fig" data-testid="srce2-fig-sites">
          <div className="srce2-fig-l">Sites</div>
          <div className="srce2-fig-v">{count(p.site_count)}</div>
        </div>
        <div className="srce2-fig" data-testid="srce2-fig-suppliers">
          <div className="srce2-fig-l">Suppliers</div>
          <div className="srce2-fig-v">{count(suppliers.length)}</div>
        </div>
        <div className="srce2-fig" data-testid="srce2-fig-spend">
          <div className="srce2-fig-l">Spend</div>
          <div className="srce2-fig-v">{formatMoney(p.spend)}</div>
          <div className="srce2-fig-s" data-testid="srce2-fig-window">{windowText}</div>
          {p.supplier_name && (
            <div className="srce2-fig-s" title={`${formatMoney(p.supplier_spend)} of it with ${p.supplier_name}`}>
              {formatMoney(p.supplier_spend)} of it with {p.supplier_name}
            </div>
          )}
        </div>
      </div>

      <div className="srce2-goal">
        <div className="srce2-goal-t">
          <div className="srce2-goal-l">Goal</div>
          {editingGoal ? (
            <>
              <textarea ref={goalRef} className="srce2-goal-input" value={goal} rows={3} maxLength={GOAL_RAW_MAX}
                aria-label="Goal" disabled={disabled} onChange={(e) => setGoal(e.target.value)} />
              {card.goal_words && (
                <div className="srce2-note">You said: <span className="srce2-said">{card.goal_words}</span></div>
              )}
            </>
          ) : (
            <div className="srce2-goal-text" data-testid="srce2-goal-text">{goal}</div>
          )}
          {goalBad ? (
            <div className="srce2-note srce2-warn" role="alert">
              {cleaned ? `Shorten the goal to ${GOAL_MAX_OWN_WORDS.toLocaleString()} characters to build.`
                : 'Write a goal to build the draft.'}
            </div>
          ) : (edited && (
            <div className="srce2-note">Your edit is used when you click Build draft RFP.</div>
          ))}
        </div>
        <button type="button" className="srce2-btn srce2-btn--ghost srce2-btn--sm" disabled={disabled}
          onClick={() => setEditingGoal((v) => !v)} aria-expanded={editingGoal}>
          {editingGoal ? 'Done' : 'Edit'}
        </button>
      </div>

      <div className="srce2-rows">
        <Row id={`${rowId}-parts`} label="Parts" value={partsLine} open={openRow === 'parts'} onToggle={() => toggle('parts')}>
          {examples.length > 0 && (
            <>
              <div className="srce2-more-h">Largest by spend</div>
              <ul className="srce2-list">{examples.map((x) => <li key={x}>{x}</li>)}</ul>
            </>
          )}
          {(p.sites || []).length > 0 && (
            <>
              <div className="srce2-more-h">Sites</div>
              <ul className="srce2-list">{p.sites.map((x) => <li key={x}>{x}</li>)}</ul>
            </>
          )}
          {examples.length === 0 && (p.sites || []).length === 0 && (
            <div className="srce2-more-p">{partsLine}</div>
          )}
        </Row>
        <Row id={`${rowId}-suppliers`} label="Suppliers" value={supplierLine}
          tag={suppliers.length === 0 ? 'Still open' : null}
          open={openRow === 'suppliers'} onToggle={() => toggle('suppliers')}>
          {suppliers.length > 0 ? (
            <ul className="srce2-list srce2-list--pairs">
              {suppliers.map((s) => (
                <li key={s.name}><span>{s.name}</span><span className="srce2-list-r">{supplierSource(s.source)}</span></li>
              ))}
            </ul>
          ) : (
            <div className="srce2-more-p">{card.still_open || 'No suppliers yet. Use Make changes to add one.'}</div>
          )}
        </Row>
        <Row id={`${rowId}-timing`} label="Timing" value={timingLine} tag={hasDue ? null : 'Still open'}
          open={openRow === 'timing'} onToggle={() => toggle('timing')}>
          {timing.length > 0 && (
            <ul className="srce2-list">{timing.map((t) => <li key={t}>{t}</li>)}</ul>
          )}
          {(card.timing_notes || []).map((note) => (
            <div key={note} className="srce2-more-p">{note}</div>
          ))}
          {!hasDue && card.still_open && <div className="srce2-more-p">{card.still_open}</div>}
        </Row>
      </div>

      <div className="srce2-acts">
        {!askFirst && (
          <button type="button" className="srce2-btn srce2-btn--primary" data-testid="srce2-build"
            disabled={disabled || goalBad} onClick={build}>
            {state === 'building' ? 'Building…' : 'Build draft RFP'}
          </button>
        )}
        {canChange && (
          <button type="button" className={`srce2-btn${changing ? ' is-on' : ''}`} aria-pressed={changing}
            data-testid="srce2-make-changes" disabled={disabled} onClick={() => onMakeChanges && onMakeChanges(card)}>
            Make changes
          </button>
        )}
        <button type="button" className="srce2-btn srce2-btn--ghost" data-testid="srce2-new-chat"
          disabled={disabled || busy} onClick={() => onStartNewChat && onStartNewChat(card)}>
          Start a new chat
        </button>
        <span className="srce2-grow" />
        <span className="srce2-fine">Nothing goes to suppliers until you publish.</span>
        <div className="srce2-menu" ref={menuRef}>
          <button ref={moreRef} type="button" className="srce2-btn srce2-btn--ghost srce2-btn--icon" aria-label="More actions"
            aria-haspopup="menu" aria-expanded={menuOpen} data-testid="srce2-more" disabled={disabled}
            onClick={() => setMenuOpen((v) => !v)}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="4.5" cy="10" r="1.6" /><circle cx="10" cy="10" r="1.6" /><circle cx="15.5" cy="10" r="1.6" /></svg>
          </button>
          {menuOpen && (
            <div className="srce2-menu-pop" role="menu">
              <button type="button" role="menuitem" data-testid="srce2-discard" onClick={discard}>Discard this draft</button>
            </div>
          )}
        </div>
      </div>
      {error && <div className="hm-rptoffer-fail srce2-error" role="alert">{error}</div>}
    </section>
  );
}

// "Make changes": the change box inside the Home chat box (one place to type). The
// four topics, or one topic's suggestions with Back; the x returns to normal chat.
export function IntakeChangeBar({ topic, onTopic, onClose, onSend, onFill, disabled = false }) {
  const current = INTAKE_CHANGE_TOPICS.find((t) => t.key === topic) || null;
  return (
    <div className="srce2-change" data-testid="srce2-change-bar">
      <div className="srce2-change-row" data-testid={current ? 'srce2-change-chips' : 'srce2-change-topics'}>
        {current ? (
          <>
            <button type="button" className="srce2-topic srce2-topic--back" onClick={() => onTopic(null)}>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 4L6 8l4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Back
            </button>
            {current.chips.map((chip) => (
              <button key={chip.label} type="button" className="srce2-topic" disabled={disabled && !!chip.send}
                onClick={() => (chip.send ? onSend(chip.send) : onFill(chip.fill))}>
                {chip.label}
              </button>
            ))}
          </>
        ) : (
          INTAKE_CHANGE_TOPICS.map((t) => (
            <button key={t.key} type="button" className="srce2-topic" onClick={() => onTopic(t.key)}>{t.label}</button>
          ))
        )}
        <span className="srce2-grow" />
        <button type="button" className="srce2-topic srce2-topic--x" aria-label="Close the changes and go back to chat"
          data-testid="srce2-change-close" onClick={onClose}>
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </button>
      </div>
    </div>
  );
}

// The slim bar above the chat box: an RFP draft saved in another chat (after "Start
// a new chat" or a reload). Open moves it into this chat with the banner's resume
// call (the server writes the answer and a fresh card); Discard is the banner's.
export function IntakeDraftBar({ authFetch, conversationId, onResumed }) {
  const [active, setActive] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let stop = false;
    setActive(null);
    setError('');
    authFetch(`${API_BASE}/api/sourcing/intakes/active`).then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!stop && d && d.intake) setActive(d.intake); })
      .catch(() => {});
    return () => { stop = true; };
  }, [authFetch, conversationId]);
  if (!active || active.conversation_id === conversationId) return null;
  const open = async () => {
    setBusy(true);
    setError('');
    try {
      const out = await postIntake(authFetch, `/api/sourcing/intakes/${encodeURIComponent(active.intake_id)}/resume`,
        { conversation_id: conversationId });
      setActive(null);
      onResumed(out, conversationId);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const discard = async () => {
    setBusy(true);
    setError('');
    try {
      await postIntake(authFetch, `/api/sourcing/intakes/${encodeURIComponent(active.intake_id)}/dismiss`);
      setActive(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="srce2-pin" role="status" data-testid="srce2-draft-bar">
      <span className="srce2-pin-dot" aria-hidden="true" />
      <span className="srce2-pin-t"><b>RFP draft:</b> {active.name}</span>
      <button type="button" className="srce2-btn srce2-btn--sm" disabled={busy} onClick={open} data-testid="srce2-draft-open">Open</button>
      <button type="button" className="srce2-btn srce2-btn--ghost srce2-btn--sm" disabled={busy} onClick={discard}>Discard</button>
      {error && <span className="hm-rptoffer-fail" role="alert">{error}</span>}
    </div>
  );
}
